import type { DiffModel, Hunk, InlineLinePair } from "@fastdiff/worker";
import type { Lines } from "./lines";
import type { DiffLineKind } from "./components/DiffLine";

export interface Row {
  left:  { kind: DiffLineKind; lineNumber: number | null; text: string; sourceLine: number | null; inlineOps?: InlineLinePair["ops"] };
  right: { kind: DiffLineKind; lineNumber: number | null; text: string; sourceLine: number | null; inlineOps?: InlineLinePair["ops"] };
}

export function buildRows(model: DiffModel, leftLines: Lines, rightLines: Lines): Row[] {
  const rows: Row[] = [];
  for (const h of model.hunks) {
    appendHunkRows(rows, h, leftLines, rightLines);
  }
  return rows;
}

export interface NavHunk {
  hunkIndex: number;
  kind: "delete" | "insert" | "change";
  rowStart: number;  // inclusive
  rowEnd: number;    // exclusive
}

export function computeNavHunks(
  model: DiffModel,
  leftLines: Lines,
  rightLines: Lines,
): NavHunk[] {
  const out: NavHunk[] = [];
  const rows: Row[] = [];
  for (let i = 0; i < model.hunks.length; i++) {
    const h = model.hunks[i] as Hunk;
    const before = rows.length;
    appendHunkRows(rows, h, leftLines, rightLines);
    const after = rows.length;
    if (h.kind !== "equal") {
      out.push({ hunkIndex: i, kind: h.kind, rowStart: before, rowEnd: after });
    }
  }
  return out;
}

function appendHunkRows(rows: Row[], h: Hunk, leftLines: Lines, rightLines: Lines): void {
  const [ls, le] = h.leftRange;
  const [rs, re] = h.rightRange;
  switch (h.kind) {
    case "equal": {
      for (let i = 0; i < le - ls; i++) {
        rows.push({
          left:  { kind: "equal", lineNumber: ls + i + 1, text: leftLines.get(ls + i),  sourceLine: ls + i },
          right: { kind: "equal", lineNumber: rs + i + 1, text: rightLines.get(rs + i), sourceLine: rs + i },
        });
      }
      return;
    }
    case "delete": {
      for (let i = 0; i < le - ls; i++) {
        rows.push({
          left:  { kind: "delete", lineNumber: ls + i + 1, text: leftLines.get(ls + i), sourceLine: ls + i },
          right: { kind: "placeholder", lineNumber: null, text: "", sourceLine: null },
        });
      }
      return;
    }
    case "insert": {
      for (let i = 0; i < re - rs; i++) {
        rows.push({
          left:  { kind: "placeholder", lineNumber: null, text: "", sourceLine: null },
          right: { kind: "insert", lineNumber: rs + i + 1, text: rightLines.get(rs + i), sourceLine: rs + i },
        });
      }
      return;
    }
    case "change": {
      const lLen = le - ls;
      const rLen = re - rs;
      const paired = Math.min(lLen, rLen);
      for (let i = 0; i < paired; i++) {
        const pair = h.inline?.[i];
        rows.push({
          left:  { kind: "change-left",  lineNumber: ls + i + 1, text: leftLines.get(ls + i),  sourceLine: ls + i, inlineOps: pair?.ops },
          right: { kind: "change-right", lineNumber: rs + i + 1, text: rightLines.get(rs + i), sourceLine: rs + i, inlineOps: pair?.ops },
        });
      }
      for (let i = paired; i < lLen; i++) {
        rows.push({
          left:  { kind: "delete", lineNumber: ls + i + 1, text: leftLines.get(ls + i), sourceLine: ls + i },
          right: { kind: "placeholder", lineNumber: null, text: "", sourceLine: null },
        });
      }
      for (let i = paired; i < rLen; i++) {
        rows.push({
          left:  { kind: "placeholder", lineNumber: null, text: "", sourceLine: null },
          right: { kind: "insert", lineNumber: rs + i + 1, text: rightLines.get(rs + i), sourceLine: rs + i },
        });
      }
      return;
    }
  }
}
